import React, { useState } from 'react'
import { ShieldCheck, LogOut } from 'lucide-react'
import { recordLegalAcceptance, signOut } from '../../lib/supabase'
import { LEGAL_DOC_KEYS } from '../../legal/documents'
import { Button } from '../ui/button'
import LegalDocLink from './LegalDocLink'

// Puerta legal: bloquea hasta que el TITULAR de la cuenta acepta los documentos
// requeridos (DPA + Contrato de Servicio). La aceptación queda a nombre del club.
// Firmar en nombre de la organización es facultad del dueño, no de cualquier admin:
// RAIDERS tiene cuatro admins y cualquiera podía obligar al club.
// Si `canAccept` es false, solo se muestra el aviso de pendiente.
//
// Cada aceptacion guarda el texto tal como se mostro. Desde la pieza 2b el DPA es el mismo
// para todos los clubes, asi que `documento_datos` lleva solo la version y el motivo.
// `motivo: 'puerta'` es lo que la base cuenta como aceptacion del convenio
// (_condiciones_torneo_aceptadas descarta las enmiendas viejas por casilla).
const datosDeLaAceptacion = (p) =>
  p.docKey === LEGAL_DOC_KEYS.DPA
    ? { dpa_version: p.version, motivo: 'puerta' }
    : { version: p.version, motivo: 'puerta' }

const LegalAcceptanceGate = ({ pendientes = [], canAccept = false, orgName = '', orgId = null, esActualizacion = false, onAccepted }) => {
  const [checked, setChecked] = useState(() => ({}))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const allChecked = pendientes.length > 0 && pendientes.every((p) => checked[p.docKey])

  const toggle = (docKey) => setChecked((prev) => ({ ...prev, [docKey]: !prev[docKey] }))

  const handleAceptar = async () => {
    if (!allChecked) return
    setSaving(true)
    setError(null)
    try {
      for (const p of pendientes) {
        const { error } = await recordLegalAcceptance(p.docKey, {
          orgId,
          textoMostrado: p.doc?.contenido || null,
          documentoDatos: datosDeLaAceptacion(p),
        })
        if (error) throw error
      }
      onAccepted?.()
    } catch {
      setError('No se pudo registrar la aceptación. Intenta de nuevo.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="min-h-screen bg-muted/40 flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-background rounded-2xl border shadow-sm p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="size-10 rounded-full bg-primary/10 flex items-center justify-center">
            <ShieldCheck className="size-5 text-primary" />
          </div>
          <div>
            <h1 className="font-semibold text-foreground">
              {esActualizacion ? 'Actualizamos los documentos' : 'Activa tu organización'}
            </h1>
            {orgName && <p className="text-xs text-muted-foreground">{orgName}</p>}
          </div>
        </div>

        {canAccept ? (
          <>
            <p className="text-sm text-muted-foreground mb-4">
              {esActualizacion
                ? 'Hay una versión nueva de estos documentos. Revísala y acéptala para seguir usando MYKLUBB. Tu aceptación queda registrada a nombre de la organización (fecha, versión y usuario).'
                : 'Para empezar a usar MYKLUBB, revisa y acepta los siguientes documentos. Tu aceptación queda registrada a nombre de la organización (fecha, versión y usuario).'}
            </p>

            <div className="space-y-2 mb-5">
              {pendientes.map((p) => (
                <div key={p.docKey} className="rounded-lg border p-3">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-medium text-foreground">{p.doc?.titulo}</span>
                    <LegalDocLink doc={p.doc} label="Leer" className="text-primary underline text-sm shrink-0" />
                  </div>
                  <label className="flex items-start gap-2 mt-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="h-4 w-4 mt-0.5 rounded border-input"
                      checked={!!checked[p.docKey]}
                      onChange={() => toggle(p.docKey)}
                    />
                    <span className="text-sm text-muted-foreground">
                      He leído y acepto {p.doc?.titulo}.
                    </span>
                  </label>
                </div>
              ))}
            </div>

            {error && <p className="text-sm text-destructive mb-3">{error}</p>}

            <Button className="w-full" disabled={!allChecked || saving} onClick={handleAceptar}>
              {saving ? 'Registrando…' : 'Aceptar y continuar'}
            </Button>
          </>
        ) : (
          <p className="text-sm text-muted-foreground mb-5">
            {esActualizacion
              ? <>Hay una <strong>versión nueva</strong> de los documentos legales. El <strong>titular de la cuenta</strong> debe revisarla y aceptarla.</>
              : <>Esta organización está <strong>pendiente de activación</strong>. El <strong>titular de la cuenta</strong> debe iniciar sesión y aceptar los términos legales para activarla.</>}
          </p>
        )}

        <button
          type="button"
          onClick={() => signOut()}
          className="mt-4 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
        >
          <LogOut className="h-3.5 w-3.5" /> Cerrar sesión
        </button>
      </div>
    </div>
  )
}

export default LegalAcceptanceGate
